import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { Column } from 'react-table';
import { FaPlus, FaSave } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
	keepPreviousData,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { columnsData } from '../../components/VehicleTable/columnsData';
import { columnsDataCompany } from '../../components/VehicleTable/columnsDataCompany';
import { GTMsendEvent } from '../../utils/trackingGA4';
import { useAuth } from '../../hooks/useAuth';
import { downloadCSV, downloadXLSX } from '../../utils';
import { useApiClient } from '../../hooks/useApiClient';
import { RoleEnum } from '../../api';
import _ from 'lodash';
import Restricted from '../../components/Restricted/Restricted';
import PaginatedVehicleTable from '../../components/VehicleTable/PaginatedVehicleTable';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import toaster from '../../utils/toaster';
import Btn from '../../components/Btn/Btn';
import i18next from 'i18next';
import './VehicleListPage.scss';
import {
	ExportFormat,
	FileExportMenu,
} from 'src/components/FileExportMenu/FileExportMenu';
import { useConfirmAction } from 'src/hooks/useConfirmAction';
import { ReporterVisibilityDTO } from 'src/api/models/ReporterVisibilityDTO';
import { isMarket } from 'src/utils/markets';
import { Markets } from 'src/types';
import {
	StellantisRequestQuoteModal,
	StellantisVehicleEmptyState,
} from 'src/components/Stellantis';
import VehicleEmptyState from 'src/components/VehicleTable/components/VehicleEmptyState';
import {
	useVehicleMassiveUpdateContext,
	VehicleMassiveUpdateProvider,
} from 'src/components/VehicleMassiveUpdate/VehicleMassiveUpdateContext';
import { stateByIdOf } from 'src/components/VehicleMassiveUpdate/vehicleBulkDelete';
import type * as MT from 'src/motornet/types';
import { FRESH_VEHICLES_QUERY } from 'src/vehicleList/queryOptions';
import { toServerColumns } from 'src/vehicleList/columns';
import { ExportProgress, fetchAllPages } from 'src/vehicleList/fetchAllPages';
import { buildVehicleExportData } from 'src/vehicleList/exportData';
import {
	availabilityOptions,
	roleScopeOf,
	stateOptions,
} from 'src/vehicleList/filterOptions';
import {
	DEFAULT_PAGE_SIZE,
	EMPTY_FILTER_STATE,
	pageAfterShrink,
	PagingState,
	toSortBy,
	toVehiclesRequest,
	VehicleFilterState,
} from 'src/vehicleList/params';

const useVehicleList = (request: MT.PlatformVehiclesRequest | null) => {
	const { apiClient } = useApiClient();

	return useQuery({
		queryKey: ['platform', 'vehicles', request],
		queryFn: () =>
			apiClient.motornetControllerService.getPlatformVehicles(request!),
		enabled: request !== null,
		placeholderData: keepPreviousData,
		...FRESH_VEHICLES_QUERY,
	});
};

const VehicleListPage = () => (
	<VehicleMassiveUpdateProvider>
		<VehicleListPageContent />
	</VehicleMassiveUpdateProvider>
);

const VehicleListPageContent = () => {
	const { apiClient, handleApi } = useApiClient();
	const location = useLocation();
	const { userRole, user, isSalesStellantis, isSalesFacile, canViewPromoNote } =
		useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { confirmAction, isConfirmShown } = useConfirmAction();
	const { setTotalCount, setAllIds, setStateById, reset } =
		useVehicleMassiveUpdateContext();

	const [paging, setPaging] = useState<PagingState>({
		page: 1,
		pageSize: DEFAULT_PAGE_SIZE,
	});
	const [filters, setFilters] =
		useState<VehicleFilterState>(EMPTY_FILTER_STATE);
	const [visibilityEdits, setVisibilityEdits] = useState<
		Record<string, boolean>
	>({});
	const [exportProgress, setExportProgress] = useState<ExportProgress | null>(
		null
	);

	const isExporting = exportProgress !== null;

	const scope = useMemo(
		() => roleScopeOf({ userRole, isSalesFacile, isSalesStellantis }),
		[userRole, isSalesFacile, isSalesStellantis]
	);

	const request = useMemo(
		() => toVehiclesRequest(filters, scope, paging),
		[filters, scope, paging]
	);

	const requestKey = JSON.stringify(request);

	useEffect(() => {
		reset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requestKey]);

	const { data, isPending, isFetching, error, refetch } =
		useVehicleList(request);

	const loading = request !== null && (isPending || isFetching);
	const total = data?.total ?? 0;

	useEffect(() => {
		if (!data || isFetching) return;

		const page = pageAfterShrink(paging.page, paging.pageSize, data.total);
		if (page !== paging.page) setPaging((prev) => ({ ...prev, page }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, isFetching]);

	const { data: wishlist } = useQuery({
		queryKey: ['vehicles', 'wishlist', user.username],
		queryFn: () =>
			handleApi(apiClient.vehicleController.getWishlist(user.username)),
		staleTime: 5 * 60 * 1000,
	});

	const invalidateWishlist = useCallback(
		() => queryClient.invalidateQueries({ queryKey: ['vehicles', 'wishlist'] }),
		[queryClient]
	);

	const addWishlistFunc = useCallback(
		(vehicleId) => {
			handleApi(apiClient.vehicleController.postWishlist(vehicleId))
				.then(() => {
					toaster.success(
						i18next.t('vehicle_added_to_favorites_consultant_will_contact')
					);
					invalidateWishlist();
				})
				.catch(() => toaster.error(i18next.t('error_saving_data')));
		},
		[apiClient, handleApi, invalidateWishlist]
	);

	const deleteWishlistFunc = useCallback(
		(vehicleId) => {
			handleApi(apiClient.vehicleController.deleteWishlist(vehicleId))
				.then(() => invalidateWishlist())
				.catch(() => toaster.error(i18next.t('error_deleting_data')));
		},
		[apiClient, handleApi, invalidateWishlist]
	);

	const wishlistIds = useMemo(
		() => new Set((wishlist ?? []).map((item: any) => item.id)),
		[wishlist]
	);

	const rows = useMemo(
		() =>
			(data?.items ?? []).map((vehicle: any) => {
				const edited = visibilityEdits[vehicle.id];

				return {
					...vehicle,
					in_wishlist: wishlistIds.has(vehicle.id),
					addWishlist: addWishlistFunc,
					deleteWishlist: deleteWishlistFunc,
					visibilita_segnalatore:
						edited !== undefined ? edited : vehicle.visibilita_segnalatore,
					visibility_modified: edited !== undefined,
				};
			}),
		[data, wishlistIds, visibilityEdits, addWishlistFunc, deleteWishlistFunc]
	);

	useEffect(() => {
		setStateById(stateByIdOf(rows));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	const handleFiltersChange = useCallback(
		(next: VehicleFilterState) => {
			const nextKey = JSON.stringify(
				toVehiclesRequest(next, scope, { ...paging, page: 1 })
			);
			if (error && nextKey === requestKey) refetch();

			setFilters(next);
			setPaging((prev) => ({ ...prev, page: 1 }));
		},
		[scope, paging, error, requestKey, refetch]
	);

	const handlePageChange = useCallback((pageIndex: number) => {
		setPaging((prev) => ({ ...prev, page: pageIndex + 1 }));
	}, []);

	const handlePageSizeChange = useCallback((pageSize: number) => {
		setPaging((prev) => ({ ...prev, pageSize, page: 1 }));
	}, []);

	const handleSortChange = useCallback(
		(sortBy: { id: string; desc: boolean }[]) => {
			const [first] = sortBy ?? [];
			const field = toSortBy(first?.id);

			setPaging((prev) => ({
				...prev,
				page: 1,
				sortBy: field,
				sortDirection: field ? (first.desc ? 'desc' : 'asc') : undefined,
			}));
		},
		[]
	);

	const handlePageRowsChange = useCallback(
		(ids: string[]) => {
			setAllIds(ids);
			setTotalCount(ids.length);
		},
		[setAllIds, setTotalCount]
	);

	const fetchAllForExport = useCallback(
		() =>
			fetchAllPages(
				(params) =>
					apiClient.motornetControllerService.getPlatformVehicles(params),
				request,
				setExportProgress
			),
		[apiClient, request]
	);

	const handleExportButton = async (exportFormat: ExportFormat) => {
		setExportProgress({ loaded: 0, total });

		try {
			const exportData = buildVehicleExportData(await fetchAllForExport(), {
				canViewPromoNote,
			});

			if (exportFormat === 'xlsx') {
				downloadXLSX(exportData, 'Lista Veicoli HMP');
			} else {
				downloadCSV(exportData, 'Lista Veicoli HMP');
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_veicoli',
				evtAct: 'download',
				evtLab: `lista_veicoli.${exportFormat}`,
			});
		} catch (err: any) {
			toaster.error(err?.message || i18next.t('error_loading_data'));
		} finally {
			setExportProgress(null);
		}
	};

	const massiveUpdateColumn = {
		accessor: 'massive_update_selector',
		id: 'massive_update_selector',
		Header: ({ page, state }) => {
			const { ids, areAllIncluded, setIds } = useVehicleMassiveUpdateContext();
			const currentPageIds = page.map((item) => item.original.id) || [];

			return (
				<div className="text-center" key={state.pageIndex}>
					<Form.Group className="mx-2">
						<Form.Check
							onChange={(e) => {
								const isChecked = e.target.checked;

								if (!isChecked) {
									setIds(
										(ids || []).filter((id) => !currentPageIds.includes(id))
									);
									return;
								}

								setIds([...new Set([...(ids || []), ...currentPageIds])]);
							}}
							checked={
								currentPageIds?.length ? areAllIncluded(currentPageIds) : false
							}
							type="checkbox"
						/>
					</Form.Group>
				</div>
			);
		},
		disableSortBy: true,
		width: 50,
		Cell: (data: any) => {
			const { ids, toggleId } = useVehicleMassiveUpdateContext();
			const id = data.row.original.id;
			return (
				<Form.Group className="mx-2">
					<Form.Check
						onChange={(e) => toggleId(id, e.target.checked)}
						checked={ids.includes(id)}
						type="checkbox"
					/>
				</Form.Group>
			);
		},
	};

	const columns = useMemo(() => {
		const defaultColumns = columnsData.filter((col) => {
			if (col.id === 'promo_note') return canViewPromoNote;
			return true;
		});

		const cols: any[] =
			RoleEnum.MASTER === userRole ? [massiveUpdateColumn] : [];
		cols.push(...defaultColumns);

		const foundIdx = cols.findIndex((col) => col.id === 'actions');

		if (
			foundIdx !== -1 &&
			RoleEnum.MASTER === userRole &&
			isMarket(Markets.it)
		) {
			const found = { ...cols[foundIdx] };
			found.width = 100;
			cols[foundIdx] = found;
		}

		const visible = cols.filter((c) => {
			if (
				['display_reporter', 'massive_update_selector'].includes(c.accessor)
			) {
				return RoleEnum.MASTER === userRole;
			}

			return true;
		});

		return toServerColumns(visible) as Column<Object>[];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userRole, canViewPromoNote]);

	const columnsCompany = useMemo(
		() => toServerColumns(columnsDataCompany) as Column<Object>[],
		[]
	);

	const filterOptions = useMemo(
		() => ({
			stato_corrente: stateOptions(scope),
			stato_disp: availabilityOptions(),
		}),
		[scope]
	);

	const presetFilter = !_.isEmpty(location?.state?.filter)
		? location.state.filter
		: undefined;

	const modifyVisibility = async () => {
		if (isConfirmShown || !isMarket(Markets.it)) return;

		const confirmed = await confirmAction({
			title: i18next.t('confirm'),
			message: i18next.t('confirm_reporter_vehicle_visibility_message'),
		});

		if (!confirmed) return;

		const modifiedRows: ReporterVisibilityDTO = Object.entries(
			visibilityEdits
		).reduce(
			(acc, [id, visible]) => {
				(visible ? acc.visibleIds : acc.notVisibleIds).push(id);
				return acc;
			},
			{ visibleIds: [], notVisibleIds: [] } as ReporterVisibilityDTO
		);

		handleApi(apiClient.vehicleController.modifyVisibility(modifiedRows))
			.then(() => {
				toaster.success(i18next.t('reporter_vehicle_visibility_modified'));
				setVisibilityEdits({});
				queryClient.invalidateQueries({ queryKey: ['platform', 'vehicles'] });
			})
			.catch(() => toaster.error(i18next.t('error_saving_data')));
	};

	const toggleVisibilityModified = (id: string) => {
		const current = rows.find((row: any) => row.id === id);

		setVisibilityEdits((prev) => ({
			...prev,
			[id]: !current?.visibilita_segnalatore,
		}));
	};

	const handleDoubleClick = (row: any) => {
		navigate(`/details/${row.original.id}`);
	};

	const handleStellantisEmptyState = [
		RoleEnum.SALES_DEALER,
		RoleEnum.SALES,
		RoleEnum.SEGNALATORE,
	].includes(userRole);

	return (
		<Container fluid className="pb-4">
			<Row>
				<Col md="auto" className="display-6">
					{t('vehicles_list')}
				</Col>
				<Col
					md="auto"
					className="ms-auto mt-4 mt-md-0 gap-3 text-md-end d-flex"
				>
					{userRole !== RoleEnum.SEGNALATORE && (
						<FileExportMenu
							isDisabled={loading || isExporting}
							allowedFormats={['csv', 'xlsx']}
							handleExport={handleExportButton}
							label={
								exportProgress
									? `${exportProgress.loaded.toLocaleString(
											'it-IT'
									  )} / ${exportProgress.total.toLocaleString('it-IT')}`
									: undefined
							}
						/>
					)}
					{Object.keys(visibilityEdits).length > 0 &&
						userRole === RoleEnum.MASTER &&
						isMarket(Markets.it) && (
							<Btn onClick={modifyVisibility}>
								<FaSave className="me-1" />
								{t('modify_visibility')}
							</Btn>
						)}
					<Restricted to={[RoleEnum.MASTER, RoleEnum.ADMIN]}>
						<Btn
							onClick={() => {
								GTMsendEvent({
									event: 'CTA_add_vehicle',
									evtCat: 'vehicle_list',
									evtAct: 'add',
									evtLab: `vehicle`,
								});
								navigate('/vehicles/create');
							}}
						>
							<FaPlus className="me-1" />
							{t('add_vehicle')}
						</Btn>
					</Restricted>
				</Col>
			</Row>

			<Restricted to={[RoleEnum.FLEET_MANAGER]}>
				<ErrorBoundary>
					<PaginatedVehicleTable
						columns={columnsCompany}
						data={rows}
						loading={loading}
						error={error}
						onRetry={refetch}
						totalCount={total}
						pageIndex={paging.page - 1}
						pageSize={paging.pageSize}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSortChange={handleSortChange}
						onFiltersChange={handleFiltersChange}
						filterIntialState={EMPTY_FILTER_STATE}
						filterOptions={filterOptions}
						hiddenColumns={['id', 'prezzo_listino']}
						handleDoubleClick={handleDoubleClick}
					></PaginatedVehicleTable>
				</ErrorBoundary>
			</Restricted>

			<Restricted
				to={[
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.ADMIN,
					RoleEnum.AREA_MANAGER,
					RoleEnum.SEGNALATORE,
				]}
			>
				<ErrorBoundary>
					<PaginatedVehicleTable
						columns={columns}
						data={rows}
						loading={loading}
						error={error}
						onRetry={refetch}
						totalCount={total}
						pageIndex={paging.page - 1}
						pageSize={paging.pageSize}
						onPageChange={handlePageChange}
						onPageSizeChange={handlePageSizeChange}
						onSortChange={handleSortChange}
						onFiltersChange={handleFiltersChange}
						onPageRowsChange={handlePageRowsChange}
						filterIntialState={EMPTY_FILTER_STATE}
						filterOptions={filterOptions}
						presetFilter={presetFilter}
						toggleVisibilityModified={
							isMarket(Markets.it) ? toggleVisibilityModified : undefined
						}
						hiddenColumns={['id', 'prezzo_listino']}
						handleDoubleClick={handleDoubleClick}
						EmptyComponent={
							handleStellantisEmptyState
								? ({ filterState }) => {
										const specificFiltersApplied = [
											filterState?.marca,
											filterState?.modello,
											filterState?.allestimento,
										].some((value) => Boolean(value));

										return specificFiltersApplied ? (
											<StellantisVehicleEmptyState />
										) : (
											<VehicleEmptyState
												message={i18next.t('vehicle_empty_state_title')}
											/>
										);
								  }
								: () => (
										<VehicleEmptyState
											message={i18next.t('vehicle_empty_state_title')}
										/>
								  )
						}
					></PaginatedVehicleTable>
				</ErrorBoundary>
				{handleStellantisEmptyState ? <StellantisRequestQuoteModal /> : null}
			</Restricted>
		</Container>
	);
};

export default VehicleListPage;
